import { z } from 'zod';

import { calculationDataSchema, documentYearQuarterSchema, lineSchema } from '~/api/common/schemas';

import { contactLanguageSchema } from '../clients/schemas';

export const quotationStatusSchema = z.enum(['Draft', 'Sent', 'Expired', 'Approved', 'Rejected']);

export const draftQuotationSchema = z.object({
  id: z.number(),
  clientId: z.number().nullable(),
  contactId: z.number().nullable(),
  brandId: z.number().nullable(),
  conditionId: z.number().nullable(),
  customConditionId: z.number().nullable(),
  customAgreementId: z.number().nullable(),
  status: quotationStatusSchema.extract(['Draft']),
  title: z.string().nullable(),
  coEntryNumber: z.string().nullable(),
  introduction: z.string().nullable(),
  description: z.string().nullable(),
  specialConditions: z.string().nullable(),
  mail: z.string().nullable(),
  quotationLines: z.array(lineSchema),
  documentDate: z.null(),
  expirationDate: z.iso.datetime({ offset: true }).nullable(),
  approvalDate: z.null(),
  calculationData: calculationDataSchema,
  updatedAt: z.iso.datetime({ offset: true }),
});

export const submittedQuotationSchema = z.object({
  id: z.number(),
  clientId: z.number(),
  contactId: z.number(),
  brandId: z.number(),
  conditionId: z.number().nullable(),
  customConditionId: z.number().nullable(),
  customAgreementId: z.number().nullable(),
  status: quotationStatusSchema.exclude(['Draft']),
  title: z.string(),
  coEntryNumber: z.string(),
  introduction: z.string(),
  description: z.string(),
  specialConditions: z.string().nullable(),
  mail: z.string(),
  quotationLines: z.array(lineSchema).min(1),
  documentDate: z.iso.datetime({ offset: true }),
  expirationDate: z.iso.datetime({ offset: true }),
  approvalDate: z.iso.datetime({ offset: true }).nullable(),
  calculationData: calculationDataSchema,
  updatedAt: z.iso.datetime({ offset: true }),
});

export const quotationSchema = z.discriminatedUnion('status', [draftQuotationSchema, submittedQuotationSchema]);

export const quotationFiltersSchema = z.object({
  clientIds: z.array(z.number()),
  brandIds: z.array(z.number()),
  years: z.array(z.number()),
  quarters: z.array(documentYearQuarterSchema),
  statuses: quotationStatusSchema.array(),
});

export const quotationForClientSchema = z.object({
  quotation: z.object({
    status: quotationStatusSchema.exclude(['Draft']),
    title: z.string(),
    introduction: z.string(),
    description: z.string(),
    specialConditions: z.string().nullable(),
    quotationLines: z.array(lineSchema).min(1),
    calculationData: z.object({
      coExclVat: z.number(),
      coVat: z.number(),
      coInclVat: z.number(),
    }),
    expirationDate: z.iso.datetime({ offset: true }),
    conditionUrl: z.url().nullable(),
  }),
  user: z.object({
    companyName: z.string(),
    fullName: z.string().nullable(),
    vatNumber: z.string(),
    logoUrl: z.url().nullable(),
    address: z.object({
      street: z.string(),
      number: z.string(),
      zipCode: z.string(),
      city: z.string(),
    }),
  }),
  client: z.object({
    name: z.string(),
    vatNumber: z.string().nullable(),
    otherNumber: z.string().nullable(),
    language: contactLanguageSchema,
    address: z.object({
      street: z.string(),
      number: z.string(),
      zipCode: z.string(),
      city: z.string(),
    }),
  }),
});
